import {
  Avatar,
  Box,
  Flex,
  Badge,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { LucideFilter } from 'lucide-react';
import { useState, useCallback } from 'react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { RoleMenuItem } from '@duke-hq/app-admin-web/components/RoleMenuItem';
import { useParams, useNavigate } from 'react-router-dom';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import {
  ConsultantRoleSchema,
  ConsultantRoleType,
  ConsultantRoles,
} from '@duke-hq/svc-api/provider/util/consultants';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';
import { djs } from '@duke-hq/djs';
import { SortOrder } from '@duke-hq/lib/pagination';

type ConsultantType =
  RouterOutputs['providers']['user']['listConsultantsWithPagination']['items'][number];

interface SortPropsInterface {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  role?: ConsultantRoleType | undefined;
  status?: string | undefined;
  page?: string | undefined;
}

interface TeamMembersProps {
  consultants: ConsultantType[];
  urlParams: SortPropsInterface;
  onFilters: (filters: SortPropsInterface, sort: boolean) => void;
}

function TableRow({
  consultant,
  onConsultant,
}: {
  consultant: ConsultantType;
  onConsultant: (id: string) => void;
}) {
  const consultantFullName =
    consultant.first_name + (consultant.last_name ? ' ' + consultant.last_name : '');
  const badgeRegionColor = consultant.totalRegions !== 0 ? 'success' : 'fg.muted';
  const badgeRegionBg = consultant.totalRegions !== 0 ? 'bg.success-subtle' : 'bg.subtle';
  const badgeSiteColor = consultant.totalSites !== 0 ? 'info' : 'fg.muted';
  const badgeSiteBg = consultant.totalSites !== 0 ? 'bg.info-subtle' : 'bg.subtle';

  return (
    <Tr
      key={consultant.id}
      onClick={() => {
        onConsultant(consultant.id);
      }}
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
    >
      <Td>
        <HStack spacing="3">
          <Flex alignItems="center">
            <Avatar
              size="sm"
              name={consultantFullName}
              src={consultant?.picture_url ?? undefined}
            />
          </Flex>
          <Box>
            <Text fontWeight="medium">{consultantFullName}</Text>
            <Text fontWeight="normal" color="fg.muted">
              {consultant.email}
            </Text>
          </Box>
        </HStack>
      </Td>
      <Td>
        <Text color="fg.muted">{consultant.totalEnrolments || 0}</Text>
      </Td>
      <Td>
        <HStack align="center" gap="4px">
          {consultant.userSiteLocation ? (
            <Badge
              borderRadius="full"
              borderColor={
                consultant.userSiteLocation.type === 'region' ? badgeRegionColor : badgeSiteColor
              }
              bg={consultant.userSiteLocation.type === 'region' ? badgeRegionBg : badgeSiteBg}
              color={
                consultant.userSiteLocation.type === 'region' ? badgeRegionColor : badgeSiteColor
              }
              borderWidth="1px"
              padding="0px 6px"
              sx={{ textTransform: 'none' }}
            >
              <Text fontSize="xs" fontWeight="bold">
                {consultant.userSiteLocation.name}
              </Text>
            </Badge>
          ) : (
            <>
              <Badge
                borderRadius="full"
                borderColor={badgeRegionColor}
                bg={badgeRegionBg}
                color={badgeRegionColor}
                borderWidth="1px"
                padding="0px 6px"
                sx={{ textTransform: 'none' }}
              >
                <Text fontSize="xs" fontWeight="bold">
                  {consultant.totalRegions} Regions
                </Text>
              </Badge>
              <Badge
                borderRadius="full"
                borderColor={badgeSiteColor}
                bg={badgeSiteBg}
                color={badgeSiteColor}
                borderWidth="1px"
                padding="0px 6px"
                sx={{ textTransform: 'none' }}
              >
                <Text fontSize="xs" fontWeight="bold">
                  {consultant.totalSites} Sites
                </Text>
              </Badge>
            </>
          )}
        </HStack>
      </Td>
      <Td>
        <Badge
          borderRadius="full"
          colorScheme={ProviderUserRoleColorSchemes[consultant.role] ?? 'gray'}
          variant="muted"
          textTransform="none"
        >
          {consultant.role.replace(/_/g, ' ')}
        </Badge>
      </Td>
      <Td>
        {!consultant.deleted_at ? (
          <Badge borderRadius="full" colorScheme="green" variant="muted" textTransform="none">
            <Trans>ENABLED</Trans>
          </Badge>
        ) : (
          <Badge borderRadius="full" colorScheme="red" variant="muted" textTransform="none">
            <Trans>DISABLED</Trans>
          </Badge>
        )}
      </Td>
      <Td>
        {consultant.lastActivityAt || consultant.last_authenticated_at ? (
          <Text color="fg.muted">
            {djs().to(djs(consultant.lastActivityAt || consultant.last_authenticated_at))}
          </Text>
        ) : (
          <Text color="fg.muted">
            <Trans>Not logged in yet</Trans>
          </Text>
        )}
      </Td>
    </Tr>
  );
}

export function ProviderConsultantsTable({
  consultants = [],
  urlParams,
  onFilters,
}: TeamMembersProps) {
  const navigate = useNavigate();
  const { sort, orderBy } = urlParams;
  const { providerId } = useParams<{ providerId: string }>();
  const [selectedRole, setSelectedRole] = useState<ConsultantRoleType | undefined>(undefined);

  const handleRoleClick = (role: string | undefined) => {
    const consultantRole = ConsultantRoleSchema.safeParse(role).data;
    setSelectedRole((prevRole: ConsultantRoleType | undefined) =>
      prevRole === consultantRole ? undefined : consultantRole,
    );
    onFilters({ ...urlParams, role: consultantRole, page: '1' }, true);
  };
  const handleClick = useCallback(
    (id: string | number) => {
      if (providerId) {
        const basePath = `/providers/${providerId}/consultants/${id}/details`;
        navigate({
          pathname: basePath,
          search: location.search,
        });
      }
    },
    [providerId, navigate],
  );

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        ...urlParams,
        orderBy: name,
        sort: newSortDirection,
      } as SortPropsInterface;

      onFilters({ ...updatedFilters }, true);
    },
    [sort, urlParams, onFilters],
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Consultant`}
              columnName="name"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Trans>Enrolments</Trans>
          </Th>
          <Th>
            <Trans>Location</Trans>
          </Th>
          <Th>
            <Flex align={'center'}>
              <SortableHeader
                label={t`Role`}
                columnName="role"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
              <Menu>
                <MenuButton
                  ml="auto"
                  as={IconButton}
                  variant="ghost"
                  color={
                    selectedRole
                      ? `${ProviderUserRoleColorSchemes[selectedRole] ?? 'gray'}.600`
                      : 'gray.500'
                  }
                  icon={<Icon as={LucideFilter} />}
                ></MenuButton>
                <MenuList>
                  {ConsultantRoles.map((role) => (
                    <RoleMenuItem
                      key={role}
                      role={role}
                      isSelected={selectedRole === role}
                      onRoleClick={handleRoleClick}
                    />
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          </Th>
          <Th>
            <SortableHeader
              label={t`Status`}
              columnName="status"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Last seen`}
              columnName="lastActivityAt"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(consultants) && consultants.length
          ? consultants.map((consultant) => (
              <TableRow key={consultant.id} consultant={consultant} onConsultant={handleClick} />
            ))
          : null}
      </Tbody>
    </Table>
  );
}
